import { useCallback, useEffect, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import {
  carregarUsoDoPlano,
  descreverUso,
  interpretarErroDeLimite,
  invalidarUsoDoPlano,
  mensagemDeUpgrade,
  type UsoDoPlano,
} from '@/lib/planoLimites';

// A tela fala o vocabulario antigo (perfil/dataConvite); o banco fala
// papel_id/convite_enviado_em. A traducao fica neste modulo, num ponto so.
interface UsuarioDaEquipe {
  id: string;
  nome: string;
  email: string;
  cargo: string;
  perfil: string;
  foto: string;
  dataConvite: string | null;
  ultimoAcesso: string | null;
  status: string;
  papelId?: string;
}

const PAPEL_PARA_PERFIL: Record<string, string> = {
  gestor: 'admin',
  administrativo: 'operador',
};

const STATUS_DO_BANCO: Record<string, string> = {
  ativo: 'ativo',
  convidado: 'pendente',
  inativo: 'inativo',
};

interface LinhaDoBanco {
  id: string;
  nome: string | null;
  email: string;
  papel_id: string;
  status: string;
  convite_enviado_em: string | null;
  criado_em: string;
  ultimo_acesso_em: string | null;
}

function paraUsuario(linha: LinhaDoBanco): UsuarioDaEquipe {
  return {
    id: linha.id,
    nome: linha.nome || linha.email.split('@')[0],
    email: linha.email,
    cargo: '',
    perfil: PAPEL_PARA_PERFIL[linha.papel_id] || 'visualizador',
    foto: '',
    // fmtData espera AAAA-MM-DD; o banco devolve timestamp completo.
    dataConvite: (linha.convite_enviado_em || linha.criado_em).slice(0, 10),
    ultimoAcesso: linha.ultimo_acesso_em,
    status: STATUS_DO_BANCO[linha.status] || 'pendente',
    papelId: linha.papel_id,
  };
}
import { fmtData, fmtDataHora, perfis, statusUsuario } from '@/pages/plan/shared';

function UsuarioFormModal({
  user,
  onSave,
  onClose,
}: {
  user: UsuarioDaEquipe | null;
  onSave: (data: UsuarioDaEquipe) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    nome: user?.nome || '',
    email: user?.email || '',
    cargo: user?.cargo || '',
    perfil: user?.perfil || 'operador',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim()) return;
    onSave({
      id: user?.id || '',
      nome: form.nome,
      email: form.email,
      cargo: form.cargo,
      perfil: form.perfil,
      foto: '',
      dataConvite: user?.dataConvite || new Date().toISOString().slice(0, 10),
      ultimoAcesso: user?.ultimoAcesso || null,
      status: user?.status || 'pendente',
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-lg shadow-lg">
        <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground-900">
            {user ? 'Editar Usuário' : 'Convidar Usuário'}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Nome completo *
            </label>
            <input
              type="text"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              required
              placeholder="Nome do usuário"
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">E-mail *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              placeholder="email@exemplo.com"
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Cargo</label>
            <input
              type="text"
              value={form.cargo}
              onChange={(e) => setForm({ ...form, cargo: e.target.value })}
              placeholder="Ex: Síndico, Zelador..."
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Perfil de acesso
            </label>
            <select
              value={form.perfil}
              onChange={(e) => setForm({ ...form, perfil: e.target.value })}
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            >
              <option value="admin">Administrador — Acesso total, gerencia plano e usuários</option>
              <option value="operador">Operador — Edita condomínios, cadastra dados</option>
              <option value="visualizador">Visualizador — Apenas consulta, sem edição</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-background-200/70">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {user ? 'Salvar' : 'Enviar Convite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UsuariosPage() {
  // Usado so para a cortesia de desabilitar o convite; a fronteira e a
  // Edge Function (P-24, ADR-0014).
  const { user: usuarioAtual } = useAuth();
  const [usuarios, setUsuarios] = useState<UsuarioDaEquipe[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [usoDoPlano, setUsoDoPlano] = useState<UsoDoPlano | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<UsuarioDaEquipe | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UsuarioDaEquipe | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const carregarEquipe = useCallback(async () => {
    setCarregando(true);
    const { data, error } = await supabase
      .from('usuarios_conta')
      .select('id, nome, email, papel_id, status, convite_enviado_em, criado_em, ultimo_acesso_em')
      .is('deletado_em', null)
      .order('criado_em', { ascending: true });

    if (error) {
      showToast(`Erro ao carregar a equipe: ${error.message}`, 'error');
    } else {
      setUsuarios((data as LinhaDoBanco[]).map(paraUsuario));
    }
    setCarregando(false);
  }, []);

  useEffect(() => {
    void carregarEquipe();
    void carregarUsoDoPlano().then(setUsoDoPlano);
  }, [carregarEquipe]);

  const handleDelete = async () => {
    if (!deleteTarget) return;

    // Desativacao logica, nao exclusao fisica (ADR-0009).
    const { error } = await supabase
      .from('usuarios_conta')
      .update({ deletado_em: new Date().toISOString(), status: 'inativo' })
      .eq('id', deleteTarget.id);

    if (error) {
      showToast(`Erro ao remover: ${error.message}`, 'error');
    } else {
      showToast(`"${deleteTarget.nome}" removido da equipe.`);
      invalidarUsoDoPlano();
      await carregarEquipe();
      void carregarUsoDoPlano(true).then(setUsoDoPlano);
    }
    setDeleteTarget(null);
  };

  const handleSave = async (data: UsuarioDaEquipe) => {
    const papelId = data.perfil === 'admin' ? 'gestor' : 'administrativo';

    if (editTarget) {
      const { error } = await supabase
        .from('usuarios_conta')
        .update({ nome: data.nome, papel_id: papelId, editado_em: new Date().toISOString() })
        .eq('id', editTarget.id);

      if (error) showToast(`Erro ao atualizar: ${error.message}`, 'error');
      else showToast(`"${data.nome}" atualizado com sucesso.`);
    } else {
      // O convite passa por Edge Function: e ela que tem a chave do Resend e
      // valida que quem convida e o Gestor.
      const { data: sessao } = await supabase.auth.getSession();
      const { data: resposta, error } = await supabase.functions.invoke('convidar-usuario', {
        body: {
          email: data.email,
          nome: data.nome,
          papelId,
          appUrl: window.location.origin,
        },
        headers: sessao.session
          ? { Authorization: `Bearer ${sessao.session.access_token}` }
          : undefined,
      });

      const limite = interpretarErroDeLimite(resposta || error);
      if (limite) {
        showToast(mensagemDeUpgrade(limite), 'error');
      } else if (error || resposta?.error) {
        showToast(resposta?.error || error?.message || 'Erro ao convidar.', 'error');
      } else if (resposta?.emailEnviado === false) {
        showToast(`Convite criado, mas o e-mail nao saiu: ${resposta.emailErro}`, 'error');
      } else {
        showToast(`Convite enviado para ${data.email}.`);
      }
    }

    invalidarUsoDoPlano();
    await carregarEquipe();
    void carregarUsoDoPlano(true).then(setUsoDoPlano);
    setShowModal(false);
    setEditTarget(null);
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-foreground-900">Usuários da Equipe</h1>
          <p className="text-sm text-foreground-500 mt-1">
            Gerencie os membros da equipe com acesso à plataforma.
          </p>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-foreground-500">
                {usoDoPlano
                  ? `${descreverUso(usoDoPlano.usuarios, 'usuarios')} no plano ${usoDoPlano.plano?.nome ?? 'atual'}`
                  : `${usuarios.filter((u) => u.status === 'ativo').length} ativo(s)`}
                {carregando ? ' · carregando...' : ''}
              </p>
            </div>
            {/* Convidar dispara e-mail para um terceiro em nome da conta, e
                exige e-mail verificado (P-24, ADR-0014). Desabilitar aqui é
                cortesia: quem decide é a Edge Function, que recusa com 403.
                Explicar antes evita a tentativa e o toast de erro. */}
            <button
              disabled={!usuarioAtual?.emailVerificadoEm}
              title={
                usuarioAtual?.emailVerificadoEm
                  ? undefined
                  : 'Confirme seu e-mail para poder convidar. Use o aviso no topo da página.'
              }
              onClick={() => {
                setEditTarget(null);
                setShowModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="ri-user-add-line"></i>
              Convidar Usuário
            </button>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Usuário
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Perfil
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden md:table-cell">
                      Convite
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                      Último acesso
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Status
                    </th>
                    <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/50">
                  {usuarios.map((usr) => {
                    const perfilInfo = perfis[usr.perfil] || perfis.visualizador;
                    const stInfo = statusUsuario[usr.status] || statusUsuario.pendente;
                    return (
                      <tr key={usr.id} className="hover:bg-background-100/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-primary-700">
                                {usr.nome
                                  .split(' ')
                                  .map((n) => n[0])
                                  .slice(0, 2)
                                  .join('')
                                  .toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground-800 truncate">
                                {usr.nome}
                              </p>
                              <p className="text-xs text-foreground-500 truncate">{usr.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${perfilInfo.cor}`}
                          >
                            {perfilInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-sm text-foreground-600">
                            {fmtData(usr.dataConvite)}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-sm text-foreground-500">
                            {fmtDataHora(usr.ultimoAcesso)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${stInfo.cor}`}
                          >
                            {stInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => {
                                setEditTarget(usr);
                                setShowModal(true);
                              }}
                              className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
                              title="Editar"
                            >
                              <i className="ri-pencil-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setDeleteTarget(usr)}
                              className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-red-600 transition-colors"
                              title="Remover"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {showModal && (
            <UsuarioFormModal
              user={editTarget}
              onSave={handleSave}
              onClose={() => {
                setShowModal(false);
                setEditTarget(null);
              }}
            />
          )}

          {deleteTarget && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div
                className="absolute inset-0 bg-black/40"
                onClick={() => setDeleteTarget(null)}
              ></div>
              <div className="relative bg-background-50 rounded-lg border border-background-200/70 p-6 w-full max-w-md mx-4 shadow-lg">
                <p className="text-sm font-semibold text-foreground-900 mb-2">Remover usuário</p>
                <p className="text-sm text-foreground-600 mb-6">
                  Tem certeza que deseja remover <strong>{deleteTarget.nome}</strong> da equipe? O
                  acesso será revogado imediatamente.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setDeleteTarget(null)}
                    className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap"
                  >
                    Remover
                  </button>
                </div>
              </div>
            </div>
          )}

          {toast && (
            <div className="fixed bottom-6 right-6 z-50">
              <div className="px-4 py-3 rounded-lg text-sm font-medium shadow-lg bg-green-50 text-green-800 border border-green-200 flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-green-600"></i>
                {toast.msg}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
